import { useCallback } from "react"

import { QueryClient, useQueryClient } from "@tanstack/react-query"
import { AxiosError } from "axios"
import { type NavigateFunction, useNavigate } from "react-router-dom"

import { getCurrentRefreshToken } from "$/utils/tokens"

import { EPath } from "@/3514/constants"
import { type TParticipantHeaderDispatcher, useParticipantHeaderDispatcher } from "@/3514/entities/participant.header"
import { type TLogoutMutation, useLogoutMutation } from "@/3514/mutations"
import { type TAppDispatcher, useAppDispatcher } from "@/3514/store/app"
import { isUnauthorizedError, resetSentrySession } from "@/3514/utils"
import { EStorageServiceType, SessionStorageServiceKeys, StorageService } from "@/services"
import { type TEmptyAsyncCallback, emptyAsyncCallback } from "@/shared/types/functions"

import { type TUserState, useUser } from "./use-user"

type TLogoutFunction = (params?: {
    onComplete?: TEmptyAsyncCallback
    keepLocalStorageAuthTokens?: boolean
}) => Promise<void>

const localStorageService: StorageService = new StorageService(EStorageServiceType.Local)
const sessionStorageService: StorageService = new StorageService(EStorageServiceType.Session)

function useLogout(): TLogoutFunction {
    const { reset: resetUser }: TUserState = useUser()

    const { resetState: resetAppState }: TAppDispatcher = useAppDispatcher()

    const { resetState: resetHeaderState }: TParticipantHeaderDispatcher = useParticipantHeaderDispatcher()

    const qc: QueryClient = useQueryClient()

    const navigate: NavigateFunction = useNavigate()

    const { mutate: logoutMutator }: TLogoutMutation = useLogoutMutation({
        async onError(error: AxiosError): Promise<void> {
            if (isUnauthorizedError(error)) navigate(EPath.Login({ isErrorReset: false }), { replace: true })
        },
        async onSettled(): Promise<void> {
            await resetUser()
        }
    })

    return useCallback(
        async ({
            onComplete = emptyAsyncCallback,
            keepLocalStorageAuthTokens = false
        }: { onComplete?: TEmptyAsyncCallback; keepLocalStorageAuthTokens?: boolean } = {}): Promise<void> =>
            logoutMutator(
                { refresh: getCurrentRefreshToken() },
                {
                    onSuccess: (): void => (
                        navigate(EPath.Login({ isErrorReset: false }), { replace: true }),
                        keepLocalStorageAuthTokens ? void 0 : localStorageService.clearStorage(),
                        sessionStorageService.clearStorage(),
                        sessionStorageService.setItem(SessionStorageServiceKeys.UserLogout(), "true"),
                        resetSentrySession(),
                        onComplete?.(),
                        qc.getQueryCache().clear(),
                        qc.clear(),
                        resetAppState(),
                        resetHeaderState()
                    )
                }
            ),
        [logoutMutator, qc, resetAppState, navigate, resetHeaderState]
    )
}

export { useLogout }
export type { TLogoutFunction }
